import type { ShiftTimeEntry } from "@/lib/backend/api";

/**
 * Uren bevestigen of bezwaar maken (admin#624).
 *
 * De medewerker mag alléén reageren op uren die de organisatie namens hem invoerde. Bij zijn eigen
 * invoer valt er niets te bevestigen — daar heeft hij het opmerkingveld al (#426).
 */

/**
 * Heeft de organisatie deze uren ingevoerd, in plaats van de medewerker zelf?
 *
 * `enteredBy` is de rol van de invoerder, niet een user-id: "Employee" is de medewerker zelf,
 * "Manager" het kantoor en "Relation" de opdrachtgever. Die laatste telt óók als geüpload — voor de
 * medewerker is het even goed iemand anders die zijn uren invulde. (De gegenereerde client kende
 * "Relation" lang niet; check bij twijfel `schema.prisma`, niet `api.ts`.)
 *
 * Zonder urenregel, of zonder ingevulde `enteredBy`, is er niets om op te reageren: dan `false`,
 * zodat de knoppen niet verschijnen op een lege regel.
 */
export function isEnteredByOrganisation(entry: ShiftTimeEntry | null | undefined): boolean {
  if (!entry?.enteredBy) {
    return false;
  }
  return entry.enteredBy !== "Employee";
}

/**
 * Mag de medewerker op deze regel reageren? Naast "door de organisatie ingevoerd" moet er ook
 * daadwerkelijk een tijd staan — op een placeholder- regel (bijvoorbeeld van een losse
 * onkostendeclaratie) valt niets te beoordelen.
 */
export function canRespondToHours(entry: ShiftTimeEntry | null | undefined): boolean {
  if (!isEnteredByOrganisation(entry)) {
    return false;
  }
  return !!entry?.startAt || !!entry?.endAt;
}

/**
 * Opent een tik op de uren het preview-scherm, of het invoerformulier?
 *
 * Twee redenen om de preview te tonen, en de tweede werd tot admin#1093 maar op één van de drie
 * plekken gesteld: de uren liggen vast omdat het kantoor ze accepteerde (`isLocked`), óf het
 * kantoor voerde ze in. In dat tweede geval corrigeert de medewerker ze niet maar reageert hij
 * erop, en die reactie zit in de preview. Kwam hij daar niet, dan kreeg hij het formulier en
 * overschreef hij de invoer van het kantoor met de zijne.
 *
 * Eén functie voor alle drie de ingangen (het dienstscherm en de twee kaarten op Afgerond), want
 * drie kopieën van deze vraag is precies hoe ze uit elkaar zijn gelopen.
 */
export function opensHoursPreview(
  entry: ShiftTimeEntry | null | undefined,
  isLocked: boolean,
): boolean {
  return isLocked || canRespondToHours(entry);
}

/**
 * Een bezwaar zonder toelichting weigert de API met een 400. Dezelfde regel hier zodat de knop uit
 * staat in plaats van dat de gebruiker tegen een foutmelding aanloopt.
 */
export function isValidDisputeNote(note: string | null | undefined): boolean {
  return (note ?? "").trim().length > 0;
}
